import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

const GOAL_COLOR = '#e8d28c';
const ACTUAL_COLOR = '#4472c4';

/**
 * Qualified Pipeline Coverage Chart
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {string} props.title - Chart title
 * @param {Function} props.onDrill - Drilldown callback: ({ quarter }) => void (only for actual bars)
 */
const QualifiedPipelineCoverageChart = ({ data, title, onDrill, currencySymbol = '' }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !data.length || !chartRef.current) return;

    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 30, right: 80, bottom: 60, left: 160 };
    const rowHeight = 80;
    const barHeight = 22;
    const width = 900 - margin.left - margin.right;
    const height = data.length * rowHeight;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Title
    // svg
    //   .append('text')
    //   .attr('x', width / 2)
    //   .attr('y', -10)
    //   .attr('text-anchor', 'middle')
    //   .style('font-size', '16px')
    //   .style('font-weight', 'bold')
    //   .text(title || 'Qualified Pipeline coverage');

    // X scale
    const maxVal = d3.max(data, (d) => Math.max(d.goal, d.actual)) || 1;
    const xScale = d3
      .scaleLinear()
      .domain([0, maxVal * 1.15])
      .range([0, width]);

    // Y scale - one band per quarter
    const yScale = d3
      .scaleBand()
      .domain(data.map((d) => d.quarter))
      .range([0, height])
      .padding(0.3);

    // Vertical axis line
    svg
      .append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#999')
      .attr('stroke-width', 1);

    // Draw bars for each quarter
    data.forEach((d, i) => {
      const yCenter = yScale(d.quarter) + yScale.bandwidth() / 2;

      // Goal bar (top)
      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', yCenter - barHeight - 2)
        .attr('width', xScale(d.goal))
        .attr('height', barHeight)
        .attr('fill', GOAL_COLOR);

      // Goal value label
      svg
        .append('text')
        .attr('x', xScale(d.goal) + 6)
        .attr('y', yCenter - barHeight / 2 - 2)
        .attr('dy', '0.35em')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .text(d.goalM.replace('$', currencySymbol));

      // Actual bar (bottom) - clickable for drilldown
      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', yCenter + 2)
        .attr('width', xScale(d.actual))
        .attr('height', barHeight)
        .attr('fill', ACTUAL_COLOR)
        .style('cursor', onDrill ? 'pointer' : 'default')
        .on('click', function() {
          if (onDrill) {
            onDrill({ quarter: d.quarter });
          }
        });

      // Actual value label - clickable for drilldown
      svg
        .append('text')
        .attr('x', xScale(d.actual) + 6)
        .attr('y', yCenter + barHeight / 2 + 2)
        .attr('dy', '0.35em')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .style('cursor', onDrill ? 'pointer' : 'default')
        .text(d.actualM.replace('$', currencySymbol))
        .on('click', function() {
          if (onDrill) {
            onDrill({ quarter: d.quarter });
          }
        });

      // Y-axis labels (left side)
      const labelX = -10;

      // Line 1: "Current Qtr." or "Current Qtr. + N:"
      svg
        .append('text')
        .attr('x', labelX)
        .attr('y', yCenter - 18)
        .attr('text-anchor', 'end')
        .style('font-size', '11px')
        .text(d.label);

      // Line 2: Quarter name with coverage multiple
      svg
        .append('text')
        .attr('x', labelX)
        .attr('y', yCenter)
        .attr('text-anchor', 'end')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .text(
          `${d.quarter}  (${d.coverageMultiple}x)`
        );

      // Line 3: Gap
      svg
        .append('text')
        .attr('x', labelX)
        .attr('y', yCenter + 18)
        .attr('text-anchor', 'end')
        .style('font-size', '11px')
        .text(`Gap: ${d.gapM.replace('$', currencySymbol)}`);
    });

    // Legend
    const legendY = height + 30;
    const legendData = [
      { label: `Qualified Pipeline Goal ${currencySymbol}M`, color: GOAL_COLOR },
      { label: `Qualified Pipeline Actual ${currencySymbol}M`, color: ACTUAL_COLOR },
    ];

    const legendGroup = svg
      .append('g')
      .attr('transform', `translate(${width / 2 - 180}, ${legendY})`);

    legendData.forEach((item, i) => {
      const xOffset = i * 220;

      legendGroup
        .append('rect')
        .attr('x', xOffset)
        .attr('y', 0)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', item.color);

      legendGroup
        .append('text')
        .attr('x', xOffset + 22)
        .attr('y', 12)
        .style('font-size', '12px')
        .text(item.label);
    });
  }, [data, title, onDrill, currencySymbol]);

  return <div ref={chartRef} style={{ display: 'flex', justifyContent: 'center' }} />;
};

export default QualifiedPipelineCoverageChart;
